import { useState } from 'react'
import {
  type FieldPath,
  type FieldValues,
  type Path,
  type PathValue,
  useFormContext,
  useWatch,
} from 'react-hook-form'

import { Box, Button, Text, useToggle } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { fromCents } from './CompensationsFormFieldsUtils'

const parsePercentageValue = (value: string): number => {
  const normalizedValue = value.replace(',', '.')
  return parseFloat(normalizedValue)
}

const formatPercentageForDisplay = (value: number): string => {
  if (value % 1 === 0) {
    return value.toString()
  }
  return parseFloat(value.toFixed(2)).toString()
}

type PercentagePopoverFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName
  readonly?: boolean
}

function PercentagePopoverField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name }: PercentagePopoverFieldProps<TFieldValues, TName>) {
  const [open, toggle] = useToggle()
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const amountPath = `${name}.amount` as Path<TFieldValues>
  const { control, setValue } = useFormContext<TFieldValues>()
  const compensationsAmountStrategy = useWatch({ control, name })

  const initialPercentage = formatPercentageForDisplay(
    // TODO: amount now can be not in cents, fix this when we move the transform responsibility to
    // input itself
    (((compensationsAmountStrategy?.amount ?? 0) * 100) /
      (compensationsAmountStrategy?.upperLimit ?? 1)) *
      100
  )
  const [percentage, setPercentage] = useState(initialPercentage)
  const [originalPercentage, setOriginalPercentage] = useState(initialPercentage)

  const handlePercentageChange = (value: string) => {
    setPercentage(value)
  }

  const handleApply = () => {
    if (percentage === '' || isNaN(parsePercentageValue(percentage))) {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      setValue(amountPath, 0 as PathValue<TFieldValues, typeof amountPath>)
      setOriginalPercentage('0')
      toggle(!open)
      return
    }

    const percentageValue = parsePercentageValue(percentage)

    if (percentageValue === 0) {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      setValue(amountPath, 0 as PathValue<TFieldValues, typeof amountPath>)
      setOriginalPercentage('0')
      toggle(!open)
      return
    }

    const newAmount = (compensationsAmountStrategy?.upperLimit ?? 0) * (percentageValue / 100)

    const newAmountFromCents = String(newAmount / 100)

    // eslint-disable-next-line no-type-assertion/no-type-assertion
    setValue(amountPath, newAmountFromCents as PathValue<TFieldValues, typeof amountPath>, {
      shouldDirty: true,
    })
    setOriginalPercentage(percentage)
    toggle(!open)
  }

  const handleOnClickOutside = () => {
    setPercentage(originalPercentage)
    toggle(!open)
  }

  const handleOpen = () => {
    const currentPercentage = formatPercentageForDisplay(
      // TODO: amount now can be not in cents, fix this when we move the transform responsibility to
      // input itself
      (((compensationsAmountStrategy?.amount ?? 0) * 100) /
        (compensationsAmountStrategy?.upperLimit ?? 1)) *
        100
    )
    setPercentage(currentPercentage)
    setOriginalPercentage(currentPercentage)
    toggle(!open)
  }

  return (
    <PopoverMenu type="dialog">
      <PopoverMenu.Trigger>
        <Button type="secondary" onClick={handleOpen} iconName="calculator" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content
        side="bottom"
        align="center"
        onInteractOutside={handleOnClickOutside}
        width="full"
      >
        <Box flexDirection="row" alignItems="center" gap="s16" paddingY="s4" paddingX="s16">
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Box width="s96">
              <TextField
                indicator="%"
                value={percentage}
                onChange={handlePercentageChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApply()
                  }
                }}
              />
            </Box>
            <Box flexGrow>
              <Box flexDirection="row" alignItems="center">
                <Text.Normal color="grey700">
                  {i18n.t('common.of')} {fromCents(compensationsAmountStrategy?.upperLimit)} =
                </Text.Normal>
                <Text.Normal color="grey800">
                  {fromCents(
                    String(
                      (compensationsAmountStrategy?.upperLimit ?? 0) *
                        (parsePercentageValue(percentage) / 100)
                    )
                  )}
                </Text.Normal>
              </Box>
            </Box>
          </Box>
          <PopoverMenu.Close>
            <Button type="primary" onClick={handleApply} data-testid="apply-button">
              {i18n.t('common.apply')}
            </Button>
          </PopoverMenu.Close>
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export { PercentagePopoverField }
